import { mdiAlertCircleOutline, mdiCancel, mdiInformationOutline, mdiPlus } from '@mdi/js'
import { Icon } from '@mdi/react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { ComposerMediaContext } from 'components/ComposerMediaContext'
import PostPreview from 'components/PostPreview'
import PPSwitch from 'components/PPSwitch'
import SocialIcon from 'components/SocialIcon'
import TextEditor, { TextEditorProps } from 'components/TextEditor'
import TweetBlock from 'components/TweetBlock'
import { useComposerVideoUpload } from 'hooks/useComposerVideoUpload'
import {
  Article,
  ARTICLE_TYPE
} from 'interfaces'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { from, Subject } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { addComment, getLinkDetails, setComposerStatus } from 'services/compose'
import {
  composerContentItemSelector,
  composerDocumentUploadSelector,
  composerImagesSelector,
  composerImageUrlsSelector,
  composerIsEmptySelector,
  composerLinkSelector,
  composerNetworkPostsSelector,
  composerPostAsPhotoSelector,
  composerPostEditSelector,
  composerPostStatusByNetworkSelector,
  composerSelectedProfilesSelector,
  composerTwitterPostStatusSelector,
  composerVideoSelector,
  composerVideoThumbnailSelector,
  composerVideoUrlSelector,
  featuredImageUrlSelector,
  useGenericStatusSelector
} from 'services/compose/selectors'
import { mergeFilesState } from 'services/compose/utils'
import { getNetworkFileErrors } from 'services/compose/validation/fileValidators'
import { blueskyUsersSearch, fbLiveSearch, linkedInLiveSearch } from 'services/search/actions'
import { openVideoCoverDialog } from 'services/ui'
import { currentUserSelector } from 'services/users/selectors'
import {
  BRAND_BLUESKY,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_YOUTUBE,
  MAX_BLUESKY_STATUS_LENGTH,
  MAX_FACEBOOK_STATUS_LENGTH,
  MAX_GMB_STATUS_LENGTH,
  MAX_INSTAGRAM_STATUS_LENGTH,
  MAX_LINKEDIN_STATUS_LENGTH,
  MAX_THREADS_STATUS_LENGTH,
  MAX_TWEET_LENGTH,
  NETWORKS_NAMES,
  STATUS_TYPE,
  TEXT_TYPE
} from 'shared/constants'
import { ComposerFacebookPost, ComposerInstagramPost, ComposerLinkedinPost, ComposerTwitterPost, PostDestinationType } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { extractUrlsFromText, getTwitterRemainingCharsRegular } from 'utils/composer'
import { sortByKeyAscending } from 'utils/sort/order'
import { SuggestedTexts } from '../Content/SuggestedTexts'
import { InstagramMediaTagsSelect } from '../CustomizePostBlock/InstagramMediaTagsSelect'
import { TextBackgroundSelector } from '../CustomizePostBlock/TextBackgroundSelector/TextBackgroundSelector'
import { BlueskyFacetsSync } from './Bluesky/BlueskyFacetsSync'
import CommentTextBox, { FacebookCommentTextBox } from './CommentTextBox'
import NetworkOptions from './NetworkOptions'
import styles from './Optimize.pcss'
import PinterestPostStatus from './PinterestPostStatus'
import { TikTokPhotoPostCaption } from './TikTok/TikTokPhotoPostCaption'
import { YoutubePostStatus } from './YoutubePostStatus'
import { BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_PINTEREST, BRAND_TWITTER, BRAND_GOOGLE, BRAND_LINKEDIN } from 'shared'

interface OptimizeProps {
  network: PostDestinationType
  contentElement?: React.ReactNode
}

const MAX_LENGTH_BY_NETWORK: Record<string, number> = {
  [BRAND_LINKEDIN]: MAX_LINKEDIN_STATUS_LENGTH,
  [BRAND_GOOGLE]: MAX_GMB_STATUS_LENGTH,
  [BRAND_INSTAGRAM]: MAX_INSTAGRAM_STATUS_LENGTH,
  [BRAND_FACEBOOK]: MAX_FACEBOOK_STATUS_LENGTH,
  [BRAND_TWITTER]: MAX_TWEET_LENGTH,
  [BRAND_THREADS]: MAX_THREADS_STATUS_LENGTH,
  [BRAND_BLUESKY]: MAX_BLUESKY_STATUS_LENGTH
}

const MIN_REMAINING_CHARS_VISIBLE_COUNTER = 51

export function Optimize(props: OptimizeProps) {
  const { network } = props
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { files: contextFiles } = React.useContext(ComposerMediaContext)
  const user = useSelector(currentUserSelector)
  const images = useSelector(composerImagesSelector)
  const video = useSelector(composerVideoSelector)
  const videoUpload = useComposerVideoUpload()
  const post = useSelector(composerNetworkPostsSelector)[network]
  const link = useSelector(composerLinkSelector)
  const content = useSelector(composerContentItemSelector)
  const imageUrls = useSelector(composerImageUrlsSelector)
  const videoUrl = useSelector(composerVideoUrlSelector)
  const videoThumbnailUrl = useSelector(composerVideoThumbnailSelector)
  const postAsPhoto = useSelector(composerPostAsPhotoSelector)
  const composerTwitterState = useSelector(composerTwitterPostStatusSelector)
  const featuredImage = useSelector(featuredImageUrlSelector)
  const profiles = useSelector(composerSelectedProfilesSelector)
  const firstProfile = profiles.find(p => p.type === network)
  const postStateByNetwork = useSelector(composerPostStatusByNetworkSelector)
  const postState = postStateByNetwork[network]
  const isComposerEmpty = useSelector(composerIsEmptySelector)
  const isUsingGenericStatus = useSelector(useGenericStatusSelector)
  const doc = useSelector(composerDocumentUploadSelector)
  const isEditing = useSelector(composerPostEditSelector)?.id !== undefined
  const [previewTab, setPreviewTab] = React.useState<'preview' | 'ai'>('preview')

  const linkRef = React.useRef(link)
  const getLinkDetails$ = React.useRef<Subject<string>>(undefined)
  const skipLinkFetch = React.useRef<boolean>(undefined)  

  const files = React.useMemo(() => {
    return mergeFilesState(images, video, contextFiles)
  }, [contextFiles, images, video])

  const errorKeys = !isComposerEmpty && postState.hasErrors
    ? postState.caption.errors.concat(postState.content.errors).concat(postState.network.errors)
    : []

  errorKeys.push(...getNetworkFileErrors(network, Object.values(files)))

  const hintKeys = !isComposerEmpty && postState.hasHints
    ? postState.caption.hints.concat(postState.content.hints, postState.network.hints)
    : []
  const infoKeys = !isComposerEmpty
    ? postState.content.info.concat(postState.network.info).concat(postState.caption.info)
    : []

  const linkedinCommentsCount = (post as ComposerLinkedinPost).comments?.length
  const linkedinCommentHint = React.useMemo(() => {
    if (network === BRAND_LINKEDIN && linkedinCommentsCount) {
      const liProfilesWithNoComments = profiles.filter(p => p.type === BRAND_LINKEDIN && !p.isMigrated)
      if (liProfilesWithNoComments.length) {
        return (
          <li key="li-fc">
            <FormattedMessage
              id="composer.hints.linkedin.comment-api"
              values={{ profiles: liProfilesWithNoComments.map(p => p.name) }}
            />
          </li>
        )
      }
    }
    return null
  }, [network, linkedinCommentsCount, profiles])

  const imageUrlsSorted = React.useMemo(() => {
    return Object.values(files).filter(f => !f.isVideo).sort(sortByKeyAscending('order')).map(f => f.url) as string[]
  }, [files])

  const featuredImageUrl = React.useMemo(() => {
    const firstImage = Object.values(files).find(f => !f.isVideo && f.order === 1)
    return firstImage?.url || featuredImage
  }, [files, featuredImage])

  const withMixedPhotoOrientation = React.useMemo(() => {
    const orientations = Object.values(files).reduce((acc, f) => {
      if (f.orientation && !f.isVideo) {
        acc[f.orientation] = true
      }
      return acc
    }, {} as { [key: string]: boolean })
    return Object.keys(orientations).length > 1
  }, [files])

  if (network === BRAND_INSTAGRAM && withMixedPhotoOrientation) {
    hintKeys.unshift('composer.hints.instagram.mixed-orientation')
  }

  React.useEffect(() => {
    skipLinkFetch.current = (content && content?.type !== ARTICLE_TYPE) || imageUrls.length > 0 || Boolean(videoUrl)
  }, [content, imageUrls, videoUrl])

  React.useEffect(() => {
    getLinkDetails$.current = new Subject()
    const sub = getLinkDetails$.current.pipe(
      switchMap((url: string) => from(dispatch(getLinkDetails({ url })).unwrap()))
    )
      .subscribe()
    return () => {
      sub.unsubscribe()
    }
  }, [dispatch])

  const tagSearchFn = React.useMemo(() => {
    switch (network) {
      case BRAND_FACEBOOK:
        return (needle: string) => dispatch(fbLiveSearch(needle))
      case BRAND_LINKEDIN:
        return (needle: string) => dispatch(linkedInLiveSearch(needle))
      case BRAND_BLUESKY:
        return (needle: string) => dispatch(blueskyUsersSearch(needle, firstProfile!.ownerId))
      default:
        return undefined
    }
  }, [network, dispatch, firstProfile])

  const onChange = React.useCallback((network: PostDestinationType) => (text: string, html: string) => {
    const url = extractUrlsFromText(text).pop()
    const lastFetchedUrl = linkRef.current ? linkRef.current.url : ''
    if (url && url !== lastFetchedUrl) {
      const validUrl = url.toLowerCase().startsWith('http') ? url : `http://${url}`
      if (!skipLinkFetch.current && (content && (content as Article).sourceLink !== validUrl)) {
        getLinkDetails$.current?.next(validUrl)
      }
    }
    dispatch(setComposerStatus({ network, text, html }))
  }, [dispatch, content])

  const isFacebookReshare = (post as ComposerFacebookPost).reshare
  const editorProps: TextEditorProps = React.useMemo(() => {
    const withTextBackgroundSelector = network === BRAND_FACEBOOK
      && !isFacebookReshare
      && !link
      && (!content || [STATUS_TYPE, TEXT_TYPE].includes(content.type))
      && imageUrls.length === 0
      && !videoUrl
    let counter: React.ReactNode = null
    let counterValue = 0

    if (network === BRAND_TWITTER) {
      const remainingChars = getTwitterRemainingCharsRegular(post.status.text)
      counter = (
        <div className={`${styles.counter} ${remainingChars < MIN_REMAINING_CHARS_VISIBLE_COUNTER ? styles.visible : ''}`}>
          {remainingChars}
        </div>
      )
    } else if (MAX_LENGTH_BY_NETWORK[network]) {
      counterValue = MAX_LENGTH_BY_NETWORK[network] - post.status.text.length
      counter = (
        <div className={`${styles.counter} ${counterValue < MIN_REMAINING_CHARS_VISIBLE_COUNTER ? styles.visible : ''}`}>
          {counterValue}
        </div>
      )
    }

    let footerElements = counter
    if (withTextBackgroundSelector) {
      footerElements = (
        <React.Fragment>
          <TextBackgroundSelector className={styles.fbbg} />
          {counter}
        </React.Fragment>
      )
    } else if (network === BRAND_BLUESKY) {
      footerElements = (
        <React.Fragment>
          {counter}
          <BlueskyFacetsSync />
        </React.Fragment>
      )
    }

    return {
      network,
      html: isUsingGenericStatus ? post.status.html : undefined,
      initialHTML: post.status.html,
      withEmojis: true,
      withGenerateText: true,
      footerElements,
      id: `composer-text-editor-${network}`,
      inputClassName: `${styles['text-input']} ${network === BRAND_INSTAGRAM ? styles.ig : ''}`,
      footerClassName: styles['text-footer'],
      placeholder: intl.formatMessage({ id: 'composer.status-placeholder-network' }),
      searchTags: tagSearchFn,
      onChange: onChange(network)
    }
  }, [
    network, link, content, imageUrls.length, videoUrl, isFacebookReshare,
    isUsingGenericStatus, post.status.html, post.status.text, intl, tagSearchFn, onChange
  ])

  const isTwitterRetweet = (post as ComposerTwitterPost).tweetType === 'retweet'
  const isTwitterQuote = (post as ComposerTwitterPost).tweetType === 'quote'
  const isTikTokPhotoPost = network === BRAND_TIKTOK && imageUrls.length > 0
  const withTextEditor = !isTikTokPhotoPost && !isTwitterRetweet && network !== BRAND_PINTEREST && network !== BRAND_YOUTUBE
  const networkName = NETWORKS_NAMES[network]
  const documentName = network === BRAND_LINKEDIN ? doc?.name : undefined
  const postComments = ((post as ComposerInstagramPost).comments || []).map(c => c.text).filter(Boolean)
  const restrictedCommentsNetworks = user?.roles?.hasMultipleComments ? [BRAND_THREADS, BRAND_TWITTER, BRAND_YOUTUBE] : []

  // NOTE: Hide "Add Comment" button for X, YT and Threads, if user doesn't have the multi-comments role enabled
  const addCommentButton = React.useMemo(() => {
    const handleAddComment = () => {
      dispatch(addComment({ network }))
    }

    const isNetworkCommentsLimited = [BRAND_TWITTER, BRAND_YOUTUBE, BRAND_THREADS].includes(network)

    if (!isNetworkCommentsLimited || user?.roles?.hasMultipleComments) {
      return (
        <Tooltip title="Add additional comment" placement="right">
          <IconButton size="small" className={styles.addCommentButton} onClick={handleAddComment}>
            <Icon path={mdiPlus} size="24px" />
          </IconButton>
        </Tooltip>
      )
    }
    return null
  }, [user?.roles?.hasMultipleComments, dispatch, network])

  return (
    <div className={styles.container} data-testid={`composer-optimize-${network}`}>
      <div className={`${styles.brand} ${styles[props.network]}`}>
        <SocialIcon
          size={`${network === BRAND_GOOGLE || network === BRAND_TWITTER ? '36px' : '42px'}`}
          icon={network}
          outline={network !== BRAND_GOOGLE && network !== BRAND_THREADS}
          className={styles.icon}
        />
      </div>
      <div className={styles['content-box']}>
        <h3 className={styles.title}>{`${networkName} `}<FormattedMessage id="label.generic.post" /></h3>
        <div className={`${styles.section} ${styles.top}`}>
          <div className={styles['section-title']}>
            <FormattedMessage id={`composer.labels.caption-${props.network}`} />
          </div>
          {withTextEditor && (
            <div className={styles['text-box']}>
              <TextEditor {...editorProps} />
            </div>
          )}
          {network === BRAND_PINTEREST && (
            <PinterestPostStatus />
          )}
          {network === BRAND_YOUTUBE && (
            <YoutubePostStatus />
          )}
          {isTwitterRetweet && content && (
            <TweetBlock
              authorName={content.feed.title}
              authorHandle={content.feed.name}
              date={new Date(content.createdAt)}
              text={content.tweetText as string}
              tweetUrl={content.socialLink}
              isRetweet
            />
          )}
          {isTwitterQuote && content && (
            <TweetBlock
              authorName={content.feed.title}
              authorHandle={content.feed.name}
              date={new Date(content.createdAt)}
              text={content.tweetText as string}
              tweetUrl={content.socialLink}
              clampLines={1}
              isQuote
              className={styles['tweet-block']}
            />
          )}
          {[BRAND_INSTAGRAM, BRAND_LINKEDIN, BRAND_BLUESKY, ...restrictedCommentsNetworks].includes(network) && (
            <div className={`${styles['comment-box']} ${network === BRAND_INSTAGRAM ? styles['cb-ig'] : ''}`}>
              {(post as ComposerInstagramPost).comments.map((comment, index) => (
                <CommentTextBox
                  key={comment.id}
                  network={network as any}
                  initialValue={comment.text}
                  maxLength={network === BRAND_BLUESKY ? MAX_BLUESKY_STATUS_LENGTH : undefined}
                  commentId={comment.id}
                  delay={comment.delay}
                  showRemoveButton={(post as ComposerInstagramPost).comments.length > 1}
                  isFirstComment={index === 0}
                />
              ))}
              {addCommentButton}
              {/* Instagram-specific: show tag select for single-image, non-video posts */}
              {network === BRAND_INSTAGRAM && !videoUrl && imageUrls.length === 1 && (
                <InstagramMediaTagsSelect />
              )}
            </div>
          )}
          {network === BRAND_FACEBOOK && (
            <div className={styles['comment-box']}>
              {(post as ComposerFacebookPost).comments.map((comment) => (
                <FacebookCommentTextBox
                  key={comment.id}
                  comment={comment}
                  showRemoveButton={(post as ComposerInstagramPost).comments.length > 1}
                  tagSearchFn={tagSearchFn as any}
                />
              ))}
              {addCommentButton}
            </div>
          )}
          {isTikTokPhotoPost && (
            <TikTokPhotoPostCaption />
          )}
        </div>
        <div className={styles.section}>
          <NetworkOptions
            title={(
              <div className={styles['section-title']}>
                {`${networkName} `}<FormattedMessage id="label.generic.settings" />
              </div>
            )}
            network={network}
          />
        </div>
        {errorKeys.length > 0 && (
          <div className={styles['msg-wrapper']}>
            <div className={styles['errors-box']}>
              <div><Icon path={mdiCancel} className={styles['icon-err']} size="28px" /></div>
              <ul className={styles.errors}>
                {errorKeys.map(key => (
                  <li key={key}>
                    <FormattedMessage id={key} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {(hintKeys.length > 0 || linkedinCommentHint) && (
          <div className={styles['msg-wrapper']}>
            <div className={styles['hints-box']}>
              <div><Icon path={mdiAlertCircleOutline} className={styles['icon-err']} size="28px" /></div>
              <ul className={styles.hints}>
                {hintKeys.map(key => (
                  <li key={key}>
                    <FormattedMessage id={key} />
                  </li>
                ))}
                {linkedinCommentHint}
              </ul>
            </div>
          </div>
        )}
        {infoKeys.length > 0 && (
          <div className={styles['msg-wrapper']}>
            <div className={styles['info-box']}>
              <div><Icon path={mdiInformationOutline} className={styles['icon-err']} size="28px" /></div>
              <ul className={styles.info}>
                {infoKeys.map(key => (
                  <li key={key}>
                    <FormattedMessage id={key} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {props.contentElement}
      </div>
      <div className={styles['preview-box']}>
        {isEditing && (
          <PPSwitch
            className={styles['preview-switch']}
            selectedValue={previewTab}
            onSelectedValueChange={(v) => setPreviewTab(v as 'preview' | 'ai')}
            options={[
              { value: 'preview', label: 'Post preview' },
              { value: 'ai', label: 'AI / Hashtags / Snippets' }
            ]}
          />
        )}
        {(!isEditing || previewTab === 'preview') && (
          <PostPreview
            postNetwork={network}
            post={post}
            postAsPhoto={postAsPhoto}
            imageUrls={imageUrlsSorted}
            videoUrl={videoUrl}
            videoThumbnailUrl={videoThumbnailUrl}
            content={content}
            link={link}
            comments={postComments}
            twitterPostStatus={composerTwitterState}
            featuredImageUrl={featuredImageUrl}
            previewProfile={firstProfile}
            reshare={network === BRAND_FACEBOOK && (post as ComposerFacebookPost).reshare}
            textBackgroundUrl={network === BRAND_FACEBOOK ? (post as ComposerFacebookPost).textBackground?.backgroundUrl : undefined}
            textColor={network === BRAND_FACEBOOK ? (post as ComposerFacebookPost).textBackground?.color : undefined}
            documentName={documentName}
            igPhotoTags={(post as ComposerInstagramPost).tags}
            igCoverUrl={(post as ComposerInstagramPost).coverUrl}
            igOnChangeCover={videoUpload?.file || videoUrl ? () => dispatch(openVideoCoverDialog({ tab: 'upload' })) : undefined}
          />
        )}
        {isEditing && previewTab === 'ai' && (
          <SuggestedTexts className={styles['suggested-texts']} />
        )}
      </div>
    </div>
  )
}

export default Optimize
